import { PerformanceMetrics } from '../types/analytics';

/**
 * Generic decorator function for usecases that adds:
 * - Performance metrics tracking
 * - Error handling
 * - Logging
 * - Retry logic (optional)
 * 
 * @param usecaseName The name of the usecase for logging and metrics
 * @param options Configuration options for the decorator
 * @returns A decorated function that wraps the original usecase
 */
export function usecase<Input, Output>(
  usecaseName: string,
  options: {
    retryCount?: number;
    retryDelay?: number;
    logLevel?: 'debug' | 'info' | 'warn' | 'error';
    onError?: (error: Error, args: Input) => void;
    onSuccess?: (result: Output, args: Input) => void;
    validateInput?: (args: Input) => boolean;
  } = {}
): (fn: (args: Input) => Promise<Output>) => (args: Input) => Promise<Output> {
  const {
    retryCount = 0,
    retryDelay = 1000,
    logLevel = 'info',
    onError,
    onSuccess,
    validateInput
  } = options;

  return (fn: (args: Input) => Promise<Output>): (args: Input) => Promise<Output> => {
    return async (args: Input): Promise<Output> => {
      const startTime = performance.now();
      let attempt = 0;
      let lastError: any | null = null;

      // Input validation
      if (validateInput && !validateInput(args)) {
        const error = new Error(`Invalid input for usecase: ${usecaseName}`);
        console.error(`[${usecaseName}] ${error.message}`, args);
        throw error;
      }

      // Log start of usecase
      console[logLevel](`[${usecaseName}] Starting execution`, args);

      // Retry logic
      while (attempt <= retryCount) {
        try {
          // Execute the usecase
          const result = await fn(args);
          
          // Calculate performance metrics
          const endTime = performance.now();
          const executionTime = endTime - startTime;
          
          // Log success
          console[logLevel](`[${usecaseName}] Completed successfully in ${executionTime.toFixed(2)}ms`);
          
          // Track metrics
          trackMetrics(usecaseName, executionTime, true);
          
          // Call success callback if provided
          if (onSuccess) {
            onSuccess(result, args);
          }
          
          return result;
        } catch (error) {
          lastError = error;
          attempt++;
          
          // Log error
          console.error(`[${usecaseName}] Attempt ${attempt} failed:`, lastError.message);
          
          // If we've reached max retries, break the loop
          if (attempt > retryCount) {
            break;
          }
          
          // Wait before retrying
          await new Promise(resolve => setTimeout(resolve, retryDelay));
        }
      }
      
      // Calculate performance metrics for failed execution
      const endTime = performance.now();
      const executionTime = endTime - startTime;
      
      // Track metrics for failed execution
      trackMetrics(usecaseName, executionTime, false);
      
      // Call error callback if provided
      if (onError && lastError) {
        onError(lastError, args);
      }

      // Re-throw the error
      throw lastError;
    };
  };
}

/**
 * Tracks performance metrics for usecases
 */
function trackMetrics(usecaseName: string, executionTime: number, success: boolean): void {
  // This is a placeholder for actual metrics tracking
  // In a real implementation, you would send this to your analytics service
  const metrics: PerformanceMetrics = {
    name: usecaseName,
    timestamp: new Date().toISOString(),
    duration: executionTime,
    success,
  };
  
  // Example: Send metrics to analytics service
  // analyticsService.trackMetrics(metrics);
  
  // For now, just log it
  console.debug(`[Metrics] ${JSON.stringify(metrics)}`);
} 